import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type {
	FieldArrayRenderProps,
	FormRenderProps,
} from "@tql/ui-components-react/kendo/form";
import { FieldArray } from "@tql/ui-components-react/kendo/form";
import type { TextAreaChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input, TextArea } from "@tql/ui-components-react/kendo/inputs";
import { Hint } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import {
	Form,
	FormElement,
	FormField,
} from "@tql/ui-components-react/tql/form/beta";
import { markerPin_02 } from "@tql/ui-components-react/untitled/icons";
import { useAtomValue } from "jotai";
import { useCallback, useRef, useState } from "react";

import type { FtlQuote } from "~/features/ftl/ftl-quotes/atoms/active-ftl-quote.ts";
import { activeFtlQuoteAtom } from "~/features/ftl/ftl-quotes/atoms/active-ftl-quote.ts";
import { useTender } from "~/features/ftl/ftl-quotes/hooks/use-tender.tsx";
import { useTrailerSizes } from "~/features/ftl/ftl-quotes/hooks/use-trailer-sizes.tsx";
import type { UpdateShipmentDetailsCommand } from "~/features/ftl/ftl-quotes/hooks/use-update-shipment-details.tsx";
import { useUpdateShipmentDetails } from "~/features/ftl/ftl-quotes/hooks/use-update-shipment-details.tsx";
import type { Stop } from "~/features/ftl/shared/ftl-stop";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { sendFtlQuoteTenderShipmentEvent } from "~/shared/utils/google-analytics-helper.ts";

import type { FTLQuoteRequestForm } from "../../../create-ftl-quote.tsx";
import { TQLGlobeSVG } from "../../tql-globe/tql-globe-svg.tsx";
import {
	customerPOValidator,
	MAX_SPECIAL_INSTRUCTIONS_LENGTH,
	specialLoadInstructionsValidator,
	trailerSizeRequired,
} from "./shipment-details-validator/shipment-details-validator.ts";
import { StopDetailsArrayWrapper } from "./stop-details/stop-details-array-wrapper/stop-details-array-wrapper.tsx";
import { StopDetailsWithNavigation } from "./stop-details/stop-details-with-navigation/stop-details-with-navigation.tsx";

// Stable wrapper component to pass FormRenderProps to StopDetailsWithNavigation
function StopDetailsWithNavigationWrapper(
	props: Readonly<
		FieldArrayRenderProps & {
			formRenderProps: FormRenderProps;
			lastSubmitTime: number;
		}
	>,
) {
	const { formRenderProps, ...fieldArrayProps } = props;
	return (
		<StopDetailsWithNavigation
			{...fieldArrayProps}
			formRenderProps={formRenderProps}
			lastSubmitTime={props.lastSubmitTime}
		/>
	);
}

export type StopDetailsForm = {
	locationName?: string;
	streetAddress?: string;
	referenceNumber?: string;
	appointmentInformation?: string;
};

type FTLQuoteRequestFormWithStops = FTLQuoteRequestForm & {
	customerPO: string;
	trailerSize: { code: string; displayName: string };
	specialLoadInstructions?: string;
	stops: StopDetailsForm[];
};

function mapFormDataToCommand(
	formData: FTLQuoteRequestFormWithStops,
	activeFTLQuote: FtlQuote | null,
): UpdateShipmentDetailsCommand {
	return {
		quoteId: activeFTLQuote?.quoteId ?? 0,
		stops: (formData.stops as Stop[] | StopDetailsForm[]).map(
			(stop, index) => ({
				id: activeFTLQuote?.stops.at(index)?.id ?? "",
				stopType: (stop as Stop).stopType,
				locationName: (stop as StopDetailsForm).locationName,
				streetAddress: (stop as StopDetailsForm).streetAddress,
				pickupNumber: (stop as StopDetailsForm).referenceNumber,
				appointmentInformation: (stop as StopDetailsForm)
					.appointmentInformation,
			}),
		),
		customerPO: formData.customerPO,
		trailerSize: formData.trailerSize?.code,
		specialInstructions: formData.specialLoadInstructions,
	};
}

type ShipmentDetailsProps = {
	rate: number | null | undefined;
	transitDays: number | null | undefined;
	submittedQuoteFormData: FTLQuoteRequestForm | null;
	onShipmentTendered?: (result: { success: boolean; message?: string }) => void;
};

export function ShipmentDetails({
	rate,
	transitDays,
	submittedQuoteFormData,
	onShipmentTendered,
}: Readonly<ShipmentDetailsProps>) {
	const { trailerSizesData } = useTrailerSizes(
		submittedQuoteFormData?.trailerType.code || "",
	);

	const activeFTLQuote = useAtomValue(activeFtlQuoteAtom);

	// Mutation pending state only reaches the DOM after a re-render, so a second
	// submit dispatched before that commit would otherwise slip through. Stays set
	// across both legs of the chained update -> tender flow.
	const inFlightRef = useRef(false);

	const { tender, isPending: isTenderPending } = useTender({
		onSuccess: () => {
			inFlightRef.current = false;
			onShipmentTendered?.({ success: true });
		},
		onError: (error) => {
			inFlightRef.current = false;
			onShipmentTendered?.({
				success: false,
				message: error.error,
			});
		},
	});

	const { updateShipmentDetails, isPending } = useUpdateShipmentDetails({
		onSuccess: () => tender(activeFTLQuote?.quoteId ?? 0),
		// On success the ref is released by useTender instead, so the guard covers
		// the tender leg too.
		onError: () => {
			inFlightRef.current = false;
		},
	});

	useAutoSpinner(isPending || isTenderPending);

	const isMobile = useIsMobile();
	const [lastSubmitTime, setLastSubmitTime] = useState(0);
	const [specialLoadInstructions, setSpecialLoadInstructions] = useState("");

	const handleSubmit = useCallback(
		(formData: FTLQuoteRequestFormWithStops) => {
			if (inFlightRef.current) return;

			inFlightRef.current = true;
			const command = mapFormDataToCommand(formData, activeFTLQuote);
			updateShipmentDetails(command);
			sendFtlQuoteTenderShipmentEvent(command.quoteId.toString());
		},
		[activeFTLQuote, updateShipmentDetails],
	);

	return (
		<Form
			initialValues={submittedQuoteFormData}
			onSubmit={handleSubmit}
			render={(shipmentDetailsFormData: FormRenderProps) => {
				return (
					<FormElement>
						<div className={"k-p-2"}>
							<div className={"k-pb-1"}>
								<em>
									This quote is not a firm offer and is subject to review and
									acceptance by TQL based on commodity, availability, market
									conditions, and additional service requirements
								</em>
							</div>
							<div className={"k-py-1"}>
								<div
									className={"k-d-flex k-align-items-center k-flex-wrap"}
									style={
										isMobile
											? { gap: "var(--kendo-spacing-4)" }
											: { gap: "var(--kendo-spacing-16)" }
									}
								>
									<div className={"k-w-16 k-h-16"}>
										<TQLGlobeSVG />
									</div>
									<div>
										<div className={"k-font-light k-font-size-lg"}>
											<h2 className={"k-pt-2"}>
												{rate == undefined
													? "No rate available at this time."
													: `$${rate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
											</h2>
										</div>
									</div>
									<div>
										<span
											className={"k-font-size-md k-font-bold k-display-block"}
										>
											Pickup Date
										</span>
										<div className={"k-font-normal k-display-block"}>
											{submittedQuoteFormData?.stops?.[0]?.stopDate
												? new Date(
														submittedQuoteFormData.stops[0].stopDate,
													).toLocaleDateString()
												: "Not set"}
										</div>
									</div>
									<div>
										<span
											className={"k-font-size-md k-font-bold k-display-block"}
										>
											{isMobile ? "Est. Transit" : "Estimated Transit"}
										</span>
										<div className={"k-font-normal k-display-block"}>
											{(() => {
												if (transitDays == undefined) {
													return "N/A";
												}
												const pluralSuffix = transitDays > 1 ? " Days" : " Day";
												return `${transitDays}${pluralSuffix}`;
											})()}
										</div>
									</div>
								</div>
							</div>
						</div>
						<div
							className={"k-py-1"}
							style={{
								paddingBottom: isMobile
									? "var(--kendo-spacing-6)"
									: "var(--kendo-spacing-2)",
							}}
						>
							<h2>
								Shipment Details -{" "}
								{submittedQuoteFormData?.stops[0]?.location?.state} to{" "}
								{
									submittedQuoteFormData?.stops[
										submittedQuoteFormData?.stops.length - 1
									]?.location?.state
								}
								{submittedQuoteFormData?.stops.length !== 2 &&
									` (${submittedQuoteFormData?.stops.length} Stops)`}
							</h2>
							<div className={"k-d-flex k-align-items-center k-gap-2"}>
								<SvgIcon className={"k-w-4 k-h-4 k-mb-2"} icon={markerPin_02} />
								<h5>Origin/Destination Information</h5>
							</div>
						</div>
						<div
							className={"k-d-grid k-gap-6 k-pb-4"}
							style={
								isMobile
									? {
											paddingBottom: "var(--kendo-spacing-22)",
										}
									: {
											gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
										}
							}
						>
							{submittedQuoteFormData?.stops.length === 2 || isMobile ? (
								<FieldArray
									component={StopDetailsArrayWrapper}
									formRenderProps={shipmentDetailsFormData}
									hasSubmitted={lastSubmitTime > 0}
									name={"stops"}
								/>
							) : (
								<FieldArray
									component={StopDetailsWithNavigationWrapper}
									formRenderProps={shipmentDetailsFormData}
									lastSubmitTime={lastSubmitTime}
									name={"stops"}
								/>
							)}
							<div className={"k-col-span-1"}>
								<div className={"k-d-flex k-flex-column k-gap-1"}>
									<div
										style={{
											paddingBottom: isMobile
												? "var(--kendo-spacing-2)"
												: "var(--kendo-spacing-11)",
										}}
									>
										<div className={"k-font-bold k-mb-1"}>
											<h4 className={"k-m-0"}>Additional Information</h4>
											<hr></hr>
										</div>
									</div>
									<div>
										<FormField
											required
											component={Input}
											label={"Customer PO"}
											name={"customerPO"}
											validator={customerPOValidator}
										/>
									</div>
									<div data-testid={"trailer-size-dropdown"}>
										<FormField
											required
											component={DropDownList}
											data={trailerSizesData ?? []}
											label={"Trailer Size"}
											name={"trailerSize"}
											textField={"displayName"}
											validator={trailerSizeRequired}
											value={"code"}
										/>
									</div>
									<div>
										<FormField
											component={TextArea}
											label={"Special Load Instructions"}
											name={"specialLoadInstructions"}
											onChange={(e: TextAreaChangeEvent) =>
												setSpecialLoadInstructions(e.target.value ?? "")
											}
											resizable={"none"}
											rows={4}
											validator={specialLoadInstructionsValidator}
											value={specialLoadInstructions}
										/>
										<Hint
											className={"k-text-secondary"}
											direction={"end"}
										>{`${specialLoadInstructions.length}/${MAX_SPECIAL_INSTRUCTIONS_LENGTH}`}</Hint>
									</div>
								</div>
							</div>
						</div>
						{isMobile ? (
							<div className={"k-p-2"}>
								<div className={"k-py-1"}>
									<div
										className={"k-d-flex k-align-items-center k-flex-wrap"}
										style={{ gap: "var(--kendo-spacing-4)" }}
									>
										<div className={"k-w-16 k-h-16"}>
											<TQLGlobeSVG />
										</div>
										<div>
											<div className={"k-font-light k-font-size-lg"}>
												<h2 className={"k-pt-2"}>
													{rate == undefined
														? "No rate available at this time."
														: `$${rate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
												</h2>
											</div>
										</div>
										<div>
											<span
												className={"k-font-size-md k-font-bold k-display-block"}
											>
												Pickup Date
											</span>
											<div className={"k-font-normal k-display-block"}>
												{submittedQuoteFormData?.stops?.[0]?.stopDate
													? new Date(
															submittedQuoteFormData.stops[0].stopDate,
														).toLocaleDateString()
													: "Not set"}
											</div>
										</div>
										<div>
											<span
												className={"k-font-size-md k-font-bold k-display-block"}
											>
												Est. Transit
											</span>
											<div className={"k-font-normal k-display-block"}>
												{(() => {
													if (transitDays == undefined) {
														return "N/A";
													}
													const pluralSuffix =
														transitDays > 1 ? " Days" : " Day";
													return `${transitDays}${pluralSuffix}`;
												})()}
											</div>
										</div>
									</div>
								</div>
								<div className={"k-pb-1"}>
									<em>
										This quote is not a firm offer and is subject to review and
										acceptance by TQL based on commodity, availability, market
										conditions, and additional service requirements
									</em>
								</div>
							</div>
						) : null}
						<div className={"k-w-full"}>
							<div className={"k-d-flex k-justify-content-end"}>
								<Button
									loading={isPending || isTenderPending}
									// eslint-disable-next-line @typescript-eslint/no-explicit-any -- react synthetic event takes any type
									onClick={(event: React.SyntheticEvent<any, Event>) => {
										if (isPending || isTenderPending) return;
										setLastSubmitTime(Date.now());
										shipmentDetailsFormData.onSubmit(event);
									}}
									onKeyDown={(event: React.KeyboardEvent) => {
										if (event.key === " " || event.key === "Enter") {
											event.preventDefault();
											if (event.repeat || isPending || isTenderPending) return;
											setLastSubmitTime(Date.now());
											shipmentDetailsFormData.onSubmit(event);
										}
									}}
									variant={"primary"}
								>
									Tender Shipment
								</Button>
							</div>
						</div>
					</FormElement>
				);
			}}
		/>
	);
}
